// Planner screen (`/planner`) — the whole-cook schedule readout.
//
// Given a desired sit-down time (HH:MM), this screen computes the full
// start→end window of every present phase and when the cook must begin.
// The phase maths come from `computeWholeCookPlan` in `@cookchow/shared`
// — the exact same calculator the server `/anchor` uses — so the preview
// here and the schedule the cook actually runs on can never drift. The
// model is parallel prep: PreCook and PrepCook share a start and run side
// by side; LiveCook begins when the longer prep block finishes and ends
// at serve. The chosen serve time is held in the store so it survives
// navigation.
//
// The Calculate readout makes no /api/anchor call — it stays a read-out.
// The Planner carries one action — "Preview" — which opens the read-only
// `/preview` walkthrough (no anchor, no serve time required, no live clock)
// so the user can read every step and open help before the cook. The
// Preview button is always available (not gated on prerequisites). Start
// Cooking on Prereqs remains the begin-the-clock-now path.
//
// UX: single time input + "Calculate" chip. No date — a picked time that
// has already passed today rolls to tomorrow (shown explicitly). If the
// cook would have to have started already to make the chosen serve time,
// the readout shows a calm "pick a later serve time" note and writes
// nothing to the store (README §6.4). The readout updates in place on
// Calculate; nothing recomputes while the user is still turning the wheel.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { computeWholeCookPlan, type PhaseId } from '@cookchow/shared';
import { Button, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { derivedPrepLeadSeconds } from '../cooking/derivePrep';
import { ensureAudioReady } from '../cooking/sounds';
import { anchor as apiAnchor } from '../api/client';
import { ApiError } from '../api/errors';
import { useAppStore } from '../state/store';

const COURSE_LABEL: Record<string, string> = {
  starter: 'Starter',
  main: 'Main',
  dessert: 'Dessert',
};

function clientZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function formatClock(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Build a Date for the chosen HH:MM, anchored to today; if that
// instant has already passed at the moment of calculation, roll forward
// by 24h so the planner only ever projects a future cook. Cookpit files
// don't span more than a day, so a single roll is sufficient.
function resolveServeDate(time: string, now: Date = new Date()): Date | null {
  if (!time) return null;
  const [hh, mm] = time.split(':').map((v) => parseInt(v, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh!, mm!, 0, 0);
  if (today.getTime() <= now.getTime()) {
    return new Date(today.getTime() + 24 * 60 * 60 * 1000);
  }
  return today;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

type PhaseLabel = 'PrepCook' | 'PreCook' | 'LiveCook';

const PHASE_LABEL: Record<PhaseId, PhaseLabel> = {
  prepCook: 'PrepCook',
  preCook: 'PreCook',
  liveCook: 'LiveCook',
};

// A single phase's wall-clock window. `start` is when the phase's timer
// begins; `end` is when it ends — for the two parallel prep phases that
// is when their own block finishes (the longer one hands to LiveCook, the
// shorter idles); for LiveCook it is the serve time.
interface PhaseRowData {
  label: PhaseLabel;
  start: Date;
  end: Date;
}

interface CalculatedPlan {
  serve: Date;
  // Present phases in display order (PreCook, PrepCook, LiveCook), each
  // carrying its start AND end. Absent prep phases are simply omitted.
  phases: PhaseRowData[];
  startCookingAt: Date;
  totalSecs: number;
  rolledToTomorrow: boolean;
}

// The readout is one of: nothing yet, a feasible plan, or an honest
// "too soon" state when the cook would have to have started already.
type Readout = { kind: 'idle' } | { kind: 'plan'; plan: CalculatedPlan } | { kind: 'infeasible' };

export default function Planner() {
  const navigate = useNavigate();
  const recipe = useAppStore((s) => s.recipe);
  const recipeId = useAppStore((s) => s.recipeId);
  const meta = useAppStore((s) => s.meta);
  const setServeTime = useAppStore((s) => s.setServeTime);
  const setAnchoredPlan = useAppStore((s) => s.setAnchoredPlan);
  const startCountdown = useAppStore((s) => s.startCountdown);
  const markVisited = useAppStore((s) => s.markVisited);
  const serveTimeInStore = useAppStore((s) => s.serveTime);

  // Mark this informational section as opened so Prereqs can render the
  // "read" tone on its launch box. Idempotent — once true, stays true
  // for the life of the recipe session.
  useEffect(() => {
    markVisited('planner');
  }, [markVisited]);

  // Seed from any previously-chosen serve time so the picker survives
  // nav within the session.
  const [time, setTime] = useState<string>(() => {
    if (!serveTimeInStore?.instant) return '';
    const d = new Date(serveTimeInStore.instant);
    return Number.isFinite(d.getTime()) ? formatClock(d) : '';
  });
  const [readout, setReadout] = useState<Readout>({ kind: 'idle' });
  // Countdown launch state (the anchor call is async, like Start Cooking).
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  if (!recipe || !meta) {
    return (
      <main style={{ padding: '1rem' }}>
        <Wordmark size="md" as="h1" />
        <p style={{ color: theme.text.muted }}>Pick a recipe first.</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </main>
    );
  }

  const courseLabel = COURSE_LABEL[meta.selectedCourse] ?? meta.selectedCourse;

  function onCalculate(): void {
    if (!recipe) return;
    const now = new Date();
    const serve = resolveServeDate(time, now);
    if (!serve) return;
    // Anchor every present timed phase under the shared parallel-prep
    // model — the same calculator the server /anchor uses, so this preview
    // and the real cook schedule never disagree.
    const wc = computeWholeCookPlan(recipe.cookpit, serve.getTime());
    // A derived PrepCook checklist (no timed prep, but prerequisites mise)
    // occupies wall-clock before LiveCook — its declared prepTime, else a
    // 30-minute default. The server anchor doesn't know about it, so fold
    // it in here as a leading PrepCook window.
    const prepLeadSecs = derivedPrepLeadSeconds(recipe);
    const cookStartMs = wc.cookStartMs - prepLeadSecs * 1000;
    const cookStart = new Date(cookStartMs);

    // Feasibility is present-relative: if the cook would have to have
    // begun already to make this serve time, surface the honest "too
    // soon" state and write nothing to the store (README §6.4). The
    // serve instant itself is always in the future (resolveServeDate
    // rolls a past time to tomorrow), so this only trips when the cook
    // is too long to fit before the chosen serve.
    if (cookStartMs <= now.getTime()) {
      setReadout({ kind: 'infeasible' });
      return;
    }

    const phases: PhaseRowData[] = [];
    if (prepLeadSecs > 0) {
      // The derived checklist runs from the cook-start up to the first
      // timed phase (LiveCook).
      phases.push({ label: 'PrepCook', start: cookStart, end: new Date(wc.cookStartMs) });
    }
    for (const p of wc.phases) {
      phases.push({
        label: PHASE_LABEL[p.phase],
        start: new Date(p.startMs),
        end: new Date(p.endMs),
      });
    }
    setReadout({
      kind: 'plan',
      plan: {
        serve,
        phases,
        startCookingAt: cookStart,
        totalSecs: wc.totalSeconds + prepLeadSecs,
        rolledToTomorrow: !isSameLocalDay(serve, now),
      },
    });
    setServeTime({ instant: serve.toISOString(), zone: clientZone() });
  }

  // Countdown — start a real, serve-anchored cook held dormant until the
  // calculated cook-start instant. Unlike Calculate this DOES anchor (the cook
  // needs an anchored plan + token), mirroring Start Cooking, but it is not
  // gated on prerequisites: the pre-start window is gather time. If the cook
  // can't be finished by the chosen serve, surface the infeasible flag and
  // stay put.
  async function onCountdown(): Promise<void> {
    if (!recipe || !recipeId || !meta) return;
    // Prime audio inside this gesture: the countdown flips to the live cook
    // automatically later (no further tap), so this is the only chance to
    // satisfy the autoplay policy before the first activation cue sounds.
    ensureAudioReady();
    const now = new Date();
    const serve = resolveServeDate(time, now);
    if (!serve) return;

    const wc = computeWholeCookPlan(recipe.cookpit, serve.getTime());
    const prepLeadSecs = derivedPrepLeadSeconds(recipe);
    const cookStartMs = wc.cookStartMs - prepLeadSecs * 1000;
    if (cookStartMs <= now.getTime()) {
      setReadout({ kind: 'infeasible' });
      return;
    }

    setStartError(null);
    setStarting(true);
    try {
      const serveTimeObj = { instant: serve.toISOString(), zone: clientZone() };
      const plan = await apiAnchor({
        recipeId,
        recipe,
        selectedCourse: meta.selectedCourse,
        serveTime: serveTimeObj,
        prereqsConfirmed: false,
      });
      setAnchoredPlan(plan, plan.planToken);
      setServeTime(plan.serveTime);
      // The anchored schedule uses the same serve + calculator, so cookStartMs
      // (server timed-phase start minus the client-side derived-prep lead) is
      // the instant the countdown runs down to.
      startCountdown(cookStartMs);
      navigate('/cook');
    } catch (err) {
      setStartError(
        err instanceof ApiError
          ? err.message
          : "Couldn't reach the cooking service — try again in a moment.",
      );
    } finally {
      setStarting(false);
    }
  }

  const canCalculate = time.length > 0;
  // The bottom action swaps once a sit-down time is entered: Preview (read-only
  // walkthrough) → Countdown (live, serve-anchored cook).
  const hasServeTime = time.length > 0;

  return (
    <main
      style={{
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxWidth: '32rem',
        margin: '0 auto',
        minHeight: '100vh',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Wordmark size="sm" as="span" />
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>{courseLabel} — when to start?</h1>
        <p style={{ margin: 0, fontSize: '0.9rem', color: theme.text.muted }}>
          Pick when you&rsquo;d like to sit down to eat.
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.9rem', color: theme.text.muted }}>Sit down at</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            aria-label="Serve time"
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: '1.1rem',
              padding: '0.4rem 0.6rem',
              borderRadius: '8px',
              border: `1px solid ${theme.bg.muted}`,
              background: theme.bg.raised,
              color: theme.text.primary,
            }}
          />
        </label>
        <button
          type="button"
          onClick={onCalculate}
          disabled={!canCalculate}
          aria-label="Calculate start time"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '0.4rem 0.9rem',
            borderRadius: '999px',
            fontSize: '0.9rem',
            fontWeight: 600,
            lineHeight: 1.2,
            background: canCalculate ? theme.accent.action : theme.bg.muted,
            color: canCalculate ? theme.bg.base : theme.text.muted,
            border: '1px solid transparent',
            cursor: canCalculate ? 'pointer' : 'not-allowed',
            minHeight: '36px',
          }}
        >
          Calculate
        </button>
      </div>

      <section
        aria-live="polite"
        style={{
          padding: '1rem',
          background: theme.bg.raised,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          minHeight: '160px',
        }}
      >
        {readout.kind === 'idle' && (
          <p style={{ margin: 0, color: theme.text.muted, fontSize: '0.95rem' }}>
            Pick a sit-down time and tap Calculate.
          </p>
        )}
        {readout.kind === 'infeasible' && (
          <p style={{ margin: 0, color: theme.text.muted, fontSize: '0.95rem' }}>
            Not enough time to complete recipe cook to achieve sit-down time entered — pick a later
            serve time.
          </p>
        )}
        {readout.kind === 'plan' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.85rem', color: theme.text.muted }}>Start cooking at</span>
              <span
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 700,
                  fontSize: '1.8rem',
                }}
              >
                {formatClock(readout.plan.startCookingAt)}
              </span>
              {readout.plan.rolledToTomorrow && (
                <span style={{ fontSize: '0.8rem', color: theme.text.muted }}>Tomorrow</span>
              )}
              <span style={{ fontSize: '0.85rem', color: theme.text.muted }}>
                {Math.round(readout.plan.totalSecs / 60)} minutes from start to serve
              </span>
            </div>
            <div style={{ height: '1px', background: theme.bg.muted, margin: '0.25rem 0' }} />
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
              }}
            >
              {readout.plan.phases.map((p) => (
                <PhaseRow
                  key={p.label}
                  label={p.label}
                  start={formatClock(p.start)}
                  end={formatClock(p.end)}
                />
              ))}
              <PhaseRow label="Serve" start={formatClock(readout.plan.serve)} />
            </ul>
          </>
        )}
      </section>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {hasServeTime ? (
          <>
            <Button
              variant="primary"
              state={starting ? 'disabled' : 'enabled'}
              onClick={() => {
                void onCountdown();
              }}
            >
              {starting ? 'Starting…' : 'Countdown'}
            </Button>
            <p style={{ margin: 0, fontSize: '0.85rem', color: theme.text.muted }}>
              Start the cook on a countdown to your sit-down time. The Pre, Prep and LiveCook timers
              begin automatically at the calculated start.
            </p>
            {startError && (
              <div
                role="alert"
                style={{
                  padding: '0.75rem',
                  background: theme.bg.muted,
                  borderRadius: '8px',
                  color: theme.text.primary,
                  fontSize: '0.9rem',
                }}
              >
                {startError}
              </div>
            )}
          </>
        ) : (
          <>
            <Button variant="primary" onClick={() => navigate('/preview')}>
              Preview
            </Button>
            <p style={{ margin: 0, fontSize: '0.85rem', color: theme.text.muted }}>
              Read through every PreCook, PrepCook and LiveCook step — and open the help panels —
              before you start. Nothing cooks in Preview.
            </p>
          </>
        )}
      </div>

      <div>
        <Button variant="secondary" onClick={() => navigate('/prereqs')}>
          Back to Prerequisites
        </Button>
      </div>
    </main>
  );
}

// A readout row. Phase rows pass both `start` and `end` and render the
// phase's full window ("14:00 → 22:00"); the Serve row passes `start`
// only and renders a single instant.
function PhaseRow({ label, start, end }: { label: string; start: string; end?: string }) {
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '0.25rem 0',
      }}
    >
      <span style={{ color: theme.text.muted }}>{label}</span>
      <span
        aria-label={end ? `${start} to ${end}` : start}
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: '0.4rem',
        }}
      >
        <span>{start}</span>
        {end && (
          <>
            <span aria-hidden="true" style={{ color: theme.text.muted }}>
              &rarr;
            </span>
            <span>{end}</span>
          </>
        )}
      </span>
    </li>
  );
}
